import {
    Box, Card, CardContent, CircularProgress, Dialog, DialogTitle, IconButton, LinearProgress, Popover,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from "@mui/material"
import {
    AccountBalanceOutlined, ApartmentOutlined, CalendarMonthOutlined, CardGiftcardOutlined, ChevronLeftOutlined,
    ChevronRightOutlined, CloseOutlined, GroupsOutlined, HomeWorkOutlined, HourglassEmptyOutlined, KeyboardArrowDownOutlined,
    PaymentsOutlined, PeopleAltOutlined, PhoneIphoneOutlined, RefreshOutlined, SavingsOutlined, SyncOutlined
} from "@mui/icons-material"
import { FC, MouseEvent, ReactNode, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import { Apis, numberWithCommas } from "../../helpers"
import { theme } from "../../theme"

const PRIMARY = theme.lightTheme.palette.primary.main
const HERO_DARK = '#332a05' // deep gold-brown (hero gradient end)

interface PlatformStats {
    totalCollected: number
    totalCollectedThisMonth: number
    mpesaWithdrawn: number
    coopWithdrawn: number
    pendingWithdrawalMpesa: number
    pendingWithdrawalCoop: number
    processingWithdrawal: number
    totalUsers: number
    totalProperties: number
    totalHouses: number
    vacantHouses: number
    occupiedHouses: number
    totalOverpaid: number
    totalLandlords: number
    activeReferrals: number
    totalReferralEarned: number
    totalReferralUnpaid: number
    mpesaBalance: number
    coopBalance: number
    totalRevenue: number
    mpesaRevenue: number
    coopRevenue: number
    totalRevenueThisMonth: number
    mpesaRevenueThisMonth: number
    coopRevenueThisMonth: number
}

const emptyStats: PlatformStats = {
    totalCollected: 0,
    totalCollectedThisMonth: 0,
    mpesaWithdrawn: 0,
    coopWithdrawn: 0,
    pendingWithdrawalMpesa: 0,
    pendingWithdrawalCoop: 0,
    processingWithdrawal: 0,
    totalUsers: 0,
    totalProperties: 0,
    totalHouses: 0,
    vacantHouses: 0,
    occupiedHouses: 0,
    totalOverpaid: 0,
    totalLandlords: 0,
    activeReferrals: 0,
    totalReferralEarned: 0,
    totalReferralUnpaid: 0,
    mpesaBalance: 0,
    coopBalance: 0,
    totalRevenue: 0,
    mpesaRevenue: 0,
    coopRevenue: 0,
    totalRevenueThisMonth: 0,
    mpesaRevenueThisMonth: 0,
    coopRevenueThisMonth: 0
}

interface StateProps {
    loading: boolean
    refreshing: boolean
    stats: PlatformStats
    selectedMonth: string
}

const currentMonthValue = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const MIN_MONTH = '2024-10'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthIndex = (v: string) => {
    const [y, m] = v.split('-').map(Number)
    return y * 12 + (m - 1)
}

interface MonthPickerProps {
    value: string
    min: string
    max: string
    onChange: (value: string) => void
}

const MonthPicker: FC<MonthPickerProps> = ({ value, min, max, onChange }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [selYear, selMonth] = value.split('-').map(Number)
    const [viewYear, setViewYear] = useState(selYear)
    const minIdx = monthIndex(min)
    const maxIdx = monthIndex(max)

    const openPicker = (e: MouseEvent<HTMLElement>) => {
        setViewYear(selYear)
        setAnchorEl(e.currentTarget)
    }
    const close = () => setAnchorEl(null)

    return (
        <>
            <Box
                onClick={openPicker}
                sx={{
                    ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    bgcolor: 'rgba(255,255,255,0.14)', borderRadius: 5, px: 1.5, py: 0.6,
                    transition: 'background-color .15s',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' }
                }}
            >
                <CalendarMonthOutlined sx={{ fontSize: 16, opacity: 0.85 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {MONTH_NAMES[selMonth - 1]} {selYear}
                </Typography>
                <KeyboardArrowDownOutlined sx={{ fontSize: 16, opacity: 0.85 }} />
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { borderRadius: 3, mt: 1 } } }}
            >
                <Box sx={{ p: 2, width: 236 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <IconButton
                            size="small"
                            disabled={(viewYear - 1) * 12 + 11 < minIdx}
                            onClick={() => setViewYear(y => y - 1)}
                        >
                            <ChevronLeftOutlined fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#101828' }}>{viewYear}</Typography>
                        <IconButton
                            size="small"
                            disabled={(viewYear + 1) * 12 > maxIdx}
                            onClick={() => setViewYear(y => y + 1)}
                        >
                            <ChevronRightOutlined fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                        {MONTH_NAMES.map((m, idx) => {
                            const idxVal = viewYear * 12 + idx
                            const disabled = idxVal < minIdx || idxVal > maxIdx
                            const active = viewYear === selYear && idx === selMonth - 1
                            return (
                                <Box
                                    key={m}
                                    onClick={() => {
                                        if (disabled) return
                                        onChange(`${viewYear}-${String(idx + 1).padStart(2, '0')}`)
                                        close()
                                    }}
                                    sx={{
                                        textAlign: 'center', fontSize: 13, py: 0.9, borderRadius: 1.5,
                                        color: disabled ? '#cbd5e1' : active ? '#fff' : '#334155',
                                        bgcolor: active ? PRIMARY : 'transparent',
                                        cursor: disabled ? 'default' : 'pointer',
                                        fontWeight: active ? 700 : 500,
                                        transition: 'background-color .12s',
                                        '&:hover': disabled || active ? {} : { bgcolor: '#f1f5f9' }
                                    }}
                                >
                                    {m}
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
            </Popover>
        </>
    )
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
    label: string
    value: ReactNode
    icon: ReactNode
    color: string
    loading: boolean
    sub?: ReactNode
    onClick?: () => void
}

const StatCard: FC<StatCardProps> = ({ label, value, icon, color, loading, sub, onClick }) => (
    <Card
        variant="outlined"
        onClick={onClick}
        sx={{
            borderRadius: 3,
            width: 220,
            flexShrink: 0,
            borderColor: '#eef0f3',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'box-shadow .18s, transform .15s',
            '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,.08)', transform: 'translateY(-2px)' },
        }}
    >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{
                width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center',
                justifyContent: 'center', bgcolor: `${color}1A`, color, mb: 1.5,
                '& svg': { fontSize: 19 }
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: .4, mb: 0.5 }}>
                {label}
            </Typography>
            {loading
                ? <CircularProgress size={18} />
                : <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#101828', lineHeight: 1.2 }}>
                    {value}
                </Typography>
            }
            {sub && !loading && <Box sx={{ mt: 1 }}>{sub}</Box>}
        </CardContent>
    </Card>
)

const HeroBubbles: FC = () => (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 3, pointerEvents: 'none' }}>
        <Box sx={{
            position: 'absolute', width: 220, height: 220, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)', top: -90, right: -60
        }} />
        <Box sx={{
            position: 'absolute', width: 130, height: 130, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.06)', bottom: -50, right: 40
        }} />
        <Box sx={{
            position: 'absolute', width: 70, height: 70, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.07)', bottom: 20, left: -25
        }} />
    </Box>
)

const SectionLabel: FC<{ title: string }> = ({ title }) => (
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: .6, mb: 1.5, mt: 4 }}>
        {title}
    </Typography>
)

// ── Monthly revenue history dialog ─────────────────────────────────────────────

interface MonthlyRevenueRow {
    year: number
    month: number
    mpesaRevenue: number
    coopRevenue: number
    totalRevenue: number
}

interface MonthlyRevenueDialogProps {
    open: boolean
    loading: boolean
    months: MonthlyRevenueRow[]
    onClose: () => void
}

const MonthlyRevenueDialog: FC<MonthlyRevenueDialogProps> = ({ open, loading, months, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Revenue by Month
            <IconButton size="small" onClick={onClose}>
                <CloseOutlined fontSize="small" />
            </IconButton>
        </DialogTitle>
        {loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress size={26} />
            </Box>
            : <TableContainer sx={{ maxHeight: 480 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Month</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Mpesa</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Coop Bank</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {months.map(row => (
                            <TableRow hover key={`${row.year}-${row.month}`}>
                                <TableCell sx={{ fontSize: 13 }}>{MONTH_NAMES[row.month]} {row.year}</TableCell>
                                <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(row.mpesaRevenue)}</TableCell>
                                <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(row.coopRevenue)}</TableCell>
                                <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>Ksh {numberWithCommas(row.totalRevenue)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        }
    </Dialog>
)

// ── Main component ────────────────────────────────────────────────────────────

const General: FC = () => {
    const [state, setState] = useState<StateProps>({
        loading: true,
        refreshing: false,
        stats: emptyStats,
        selectedMonth: currentMonthValue()
    })
    const { loading, refreshing, stats, selectedMonth } = state

    const [revenueHistory, setRevenueHistory] = useState<{ open: boolean, loading: boolean, months: MonthlyRevenueRow[] }>({
        open: false,
        loading: false,
        months: []
    })

    const fetchStats = ({ refreshing, newMonth }: { refreshing?: boolean, newMonth?: string } = {}) => {
        const Month = newMonth || state.selectedMonth
        setState({ ...state, loading: !refreshing, refreshing: Boolean(refreshing), selectedMonth: Month })
        const [year, month] = Month.split('-').map(Number)
        Apis({ module: 'Banks', action: 'getBankStats', year, month: month - 1 })
            .then((resp: any) => setState(s => ({
                ...s,
                stats: { ...emptyStats, ...resp },
                loading: false,
                refreshing: false,
                selectedMonth: Month
            })))
            .catch(() => setState(s => ({ ...s, loading: false, refreshing: false })))
    }

    const openRevenueHistory = () => {
        setRevenueHistory(s => ({ ...s, open: true, loading: true }))
        Apis({ module: 'Banks', action: 'getMonthlyRevenue' })
            .then((resp: any) => setRevenueHistory(s => ({ ...s, months: resp.months || [], loading: false })))
            .catch(() => setRevenueHistory(s => ({ ...s, loading: false })))
    }

    useEffect(() => { fetchStats() }, [])

    const occupancyPct = stats.totalHouses > 0
        ? Math.round((stats.occupiedHouses / stats.totalHouses) * 100)
        : 0

    return (
        <Box sx={{ pr: 5, height: '100%', pb: 5 }}>
            <PageTitle title="General" />
            <Box sx={{ mt: 4, ml: 5 }}>

                {/* ── Hero: total collected ── */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <Card sx={{
                        position: 'relative',
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${HERO_DARK} 100%)`,
                        color: '#fff',
                        borderRadius: 3,
                        maxWidth: 460,
                        flex: 1,
                        minWidth: 320
                    }}>
                        <HeroBubbles />
                        <CardContent sx={{ p: 4, position: 'relative' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <PaymentsOutlined sx={{ mr: 1, opacity: 0.75, fontSize: 20 }} />
                                <Typography sx={{ opacity: 0.8, fontSize: 14 }}>Total Collected by Sheltergram</Typography>
                                <Tooltip title="Refresh">
                                    <IconButton
                                        size="small"
                                        sx={{ ml: 'auto', color: 'rgba(255,255,255,0.7)' }}
                                        onClick={() => fetchStats({ refreshing: true })}
                                        disabled={refreshing}
                                    >
                                        {refreshing
                                            ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                            : <RefreshOutlined fontSize="small" />
                                        }
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {loading
                                ? <CircularProgress size={28} sx={{ color: '#fff' }} />
                                : <Typography variant="h3" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
                                    Ksh<br />{numberWithCommas(stats.totalCollected)}
                                </Typography>
                            }
                        </CardContent>
                    </Card>

                    <Card sx={{
                        position: 'relative',
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${HERO_DARK} 100%)`,
                        color: '#fff',
                        borderRadius: 3,
                        maxWidth: 460,
                        flex: 1,
                        minWidth: 320
                    }}>
                        <HeroBubbles />
                        <CardContent sx={{ p: 4, position: 'relative' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <PaymentsOutlined sx={{ mr: 1, opacity: 0.75, fontSize: 20 }} />
                                <Typography sx={{ opacity: 0.8, fontSize: 14 }}>Collected This Month</Typography>
                                <MonthPicker
                                    value={selectedMonth}
                                    min={MIN_MONTH}
                                    max={currentMonthValue()}
                                    onChange={(newMonth) => fetchStats({ newMonth, refreshing: true })}
                                />
                            </Box>
                            {loading
                                ? <CircularProgress size={28} sx={{ color: '#fff' }} />
                                : <Typography variant="h3" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
                                    Ksh<br />{numberWithCommas(stats.totalCollectedThisMonth)}
                                </Typography>
                            }
                        </CardContent>
                    </Card>
                </Box>

                {/* ── Platform ── */}
                <SectionLabel title="Platform" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <StatCard
                        label="Total Users"
                        value={numberWithCommas(stats.totalUsers)}
                        icon={<PeopleAltOutlined />}
                        color="#1565c0"
                        loading={loading}
                    />
                    <StatCard
                        label="Landlords"
                        value={numberWithCommas(stats.totalLandlords)}
                        icon={<GroupsOutlined />}
                        color={PRIMARY}
                        loading={loading}
                    />
                    <StatCard
                        label="Properties"
                        value={numberWithCommas(stats.totalProperties)}
                        icon={<HomeWorkOutlined />}
                        color="#00838f"
                        loading={loading}
                    />
                    <StatCard
                        label="Houses"
                        value={numberWithCommas(stats.totalHouses)}
                        icon={<ApartmentOutlined />}
                        color="#455a64"
                        loading={loading}
                        sub={
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                    <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>{numberWithCommas(stats.occupiedHouses)} occupied</Typography>
                                    <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>{numberWithCommas(stats.vacantHouses)} vacant</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={occupancyPct}
                                    sx={{
                                        height: 4, borderRadius: 2, bgcolor: '#f1f5f9',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: occupancyPct >= 80 ? '#2e7d32' : occupancyPct >= 50 ? '#e65100' : '#c62828',
                                            borderRadius: 2
                                        }
                                    }}
                                />
                            </Box>
                        }
                    />
                </Box>

                {/* ── Revenue ── */}
                <SectionLabel title="Revenue" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <StatCard
                        label="Total Revenue (Since Start)"
                        value={`Ksh ${numberWithCommas(stats.totalRevenue)}`}
                        icon={<SavingsOutlined />}
                        color="#2e7d32"
                        loading={loading}
                    />
                    <StatCard
                        label={`Revenue – ${MONTH_NAMES[Number(selectedMonth.split('-')[1]) - 1]} ${selectedMonth.split('-')[0]}`}
                        value={`Ksh ${numberWithCommas(stats.totalRevenueThisMonth)}`}
                        icon={<SavingsOutlined />}
                        color="#2e7d32"
                        loading={loading}
                        onClick={openRevenueHistory}
                    />
                    <StatCard
                        label="Mpesa Revenue"
                        value={`Ksh ${numberWithCommas(stats.mpesaRevenue)}`}
                        icon={<PhoneIphoneOutlined />}
                        color="#00838f"
                        loading={loading}
                    />
                    <StatCard
                        label="Coop Revenue"
                        value={`Ksh ${numberWithCommas(stats.coopRevenue)}`}
                        icon={<AccountBalanceOutlined />}
                        color="#1565c0"
                        loading={loading}
                    />
                    <StatCard
                        label="Mpesa Balance"
                        value={`Ksh ${numberWithCommas(stats.mpesaBalance)}`}
                        icon={<PhoneIphoneOutlined />}
                        color="#00838f"
                        loading={loading}
                    />
                    <StatCard
                        label="Coop Balance"
                        value={`Ksh ${numberWithCommas(stats.coopBalance)}`}
                        icon={<AccountBalanceOutlined />}
                        color="#1565c0"
                        loading={loading}
                    />
                </Box>

                {/* ── Withdrawals ── */}
                <SectionLabel title="Withdrawals" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <StatCard
                        label="Withdrawn via Mpesa"
                        value={`Ksh ${numberWithCommas(stats.mpesaWithdrawn)}`}
                        icon={<PhoneIphoneOutlined />}
                        color="#00838f"
                        loading={loading}
                    />
                    <StatCard
                        label="Withdrawn via Coop Bank"
                        value={`Ksh ${numberWithCommas(stats.coopWithdrawn)}`}
                        icon={<AccountBalanceOutlined />}
                        color="#1565c0"
                        loading={loading}
                    />
                    <StatCard
                        label="Pending – Mpesa"
                        value={`Ksh ${numberWithCommas(stats.pendingWithdrawalMpesa)}`}
                        icon={<HourglassEmptyOutlined />}
                        color="#e65100"
                        loading={loading}
                    />
                    <StatCard
                        label="Pending – Coop Bank"
                        value={`Ksh ${numberWithCommas(stats.pendingWithdrawalCoop)}`}
                        icon={<HourglassEmptyOutlined />}
                        color="#e65100"
                        loading={loading}
                    />
                    <StatCard
                        label="Processing Withdrawal"
                        value={`Ksh ${numberWithCommas(stats.processingWithdrawal)}`}
                        icon={<SyncOutlined />}
                        color="#6a1b9a"
                        loading={loading}
                    />
                </Box>

                {/* ── Referrals ── */}
                <SectionLabel title="Referrals" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <StatCard
                        label="Active Referrals"
                        value={numberWithCommas(stats.activeReferrals)}
                        icon={<CardGiftcardOutlined />}
                        color={PRIMARY}
                        loading={loading}
                    />
                    <StatCard
                        label="Total Earned"
                        value={`Ksh ${numberWithCommas(stats.totalReferralEarned)}`}
                        icon={<PaymentsOutlined />}
                        color="#2e7d32"
                        loading={loading}
                    />
                    <StatCard
                        label="Unpaid Earnings"
                        value={`Ksh ${numberWithCommas(stats.totalReferralUnpaid)}`}
                        icon={<HourglassEmptyOutlined />}
                        color="#e65100"
                        loading={loading}
                    />
                </Box>

                {/* ── Tenant credits callout ── */}
                <SectionLabel title="Tenant Credits" />
                <Card variant="outlined" sx={{ borderRadius: 3, maxWidth: 460, borderColor: '#f4dfa8', bgcolor: '#fdf8ec' }}>
                    <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{
                            width: 42, height: 42, borderRadius: 2, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', bgcolor: `${PRIMARY}1f`, color: PRIMARY, flexShrink: 0
                        }}>
                            <SavingsOutlined />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#667085', mb: 0.3 }}>
                                Total Overpaid (Tenant Credits)
                            </Typography>
                            {loading
                                ? <CircularProgress size={18} />
                                : <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#101828' }}>
                                    Ksh {numberWithCommas(stats.totalOverpaid)}
                                </Typography>
                            }
                            <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.3 }}>
                                Credit balances tenants can apply toward future rent.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

            </Box>

            <MonthlyRevenueDialog
                open={revenueHistory.open}
                loading={revenueHistory.loading}
                months={revenueHistory.months}
                onClose={() => setRevenueHistory(s => ({ ...s, open: false }))}
            />
        </Box>
    )
}

export default General
